import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import type { Review } from "@/modules/review/types";
import VideoModal from "@/modules/review/components/video-modal";
import { useWidgetFont } from "../hooks/use-widget-font";
import type { WidgetTemplateProps } from "./template-router";

// Helper functions
function getInitials(name: string) {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase();
}

function getGradient(name: string) {
	const gradients = [
		"from-violet-500 to-purple-600",
		"from-blue-500 to-cyan-500",
		"from-emerald-500 to-teal-500",
		"from-orange-500 to-amber-500",
		"from-pink-500 to-rose-500",
		"from-indigo-500 to-blue-500",
	];
	const index = name.charCodeAt(0) % gradients.length;
	return gradients[index];
}

function AudioPlayer({ src, initialDuration }: { src: string; initialDuration?: number | null }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);

	const formatTime = (time: number) => {
		if (isNaN(time) || !isFinite(time)) return "0:00";
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	const togglePlay = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleTimeUpdate = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			setCurrentTime(audioRef.current.currentTime);
			if (!duration) {
				setDuration(audioDur);
			}
		}
	};

	const handleLoadedMetadata = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0 && !initialDuration) {
			setDuration(audioDur);
		}
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const time = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = time;
			setCurrentTime(time);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
		setCurrentTime(0);
	};

	const progress = duration > 0 && isFinite(duration) ? Math.min((currentTime / duration) * 100, 100) : 0;

	return (
		<div className="mt-3 mb-2 p-3 rounded-xl bg-gray-100">
			<audio ref={audioRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />
			<div className="flex items-center gap-3">
				<button
					type="button"
					onClick={togglePlay}
					className="block w-8 h-8 flex items-center justify-center rounded-full shrink-0 shadow-sm text-black bg-white"
				>
					{isPlaying ? (
						<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
						</svg>
					) : (
						<svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
					)}
				</button>
				<div className="flex-1 flex items-center gap-2">
					<span className="text-xs tabular-nums text-gray-500">{formatTime(currentTime)}</span>
					<div className="flex-1 relative h-1.5 rounded-full overflow-hidden bg-gray-300/50">
						<div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: "#1f1f1f" }} />
						<input type="range" min={0} max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
					</div>
					{duration >= 1 && <span className="text-xs tabular-nums text-gray-500">{formatTime(duration)}</span>}
				</div>
			</div>
		</div>
	);
}

function makeSampleReview(id: string, name: string, title: string, company: string, text: string, rating: number, avatarUrl: string): Review {
	return {
		id,
		workspace_id: "",
		form_id: null,
		date: "",
		type: "text",
		rating,
		content_type: "text",
		content_text: text,
		private_feedback: null,
		consent: null,
		video_url: null,
		video_thumbnail_url: null,
		video_duration: null,
		video_file_size: null,
		video_status: null,
		mux_upload_id: null,
		mux_asset_id: null,
		mux_playback_id: null,
		audio_url: null,
		audio_duration: null,
		customer_name: name,
		customer_headline: `${title} at ${company}`,
		customer_avatar_url: avatarUrl,
		customer_email: "",
		customer_phone: null,
		customer_company_name: company,
		customer_job_title: title,
		customer_company_website_url: null,
		customer_company_logo_url: null,
		source: "manual",
		source_post_url: null,
		status: "published",
		published_at: null,
		tags: [],
		created_by_id: "",
		created_at: "",
		updated_by_id: "",
		updated_at: "",
		deleted_by_id: null,
		deleted_at: null,
	};
}

const sampleReviews: Review[] = [
	makeSampleReview("1", "Sarah Chen", "Product Manager", "TechFlow", "This platform has completely transformed how we collect and showcase customer feedback.", 5, "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("2", "Michael Roberts", "CEO", "StartupX", "Absolutely love it! Our conversion rates increased by 40% after adding the testimonial widget.", 5, "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("3", "Emily Watson", "Marketing Director", "GrowthLabs", "The best testimonial management tool I've ever used. Clean interface and powerful features.", 4, "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("4", "David Park", "CTO", "InnovateCo", "We saw immediate results after embedding the widget. Our social proof game is strong now!", 5, "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("5", "Lisa Johnson", "VP Marketing", "ScaleUp", "Simple, elegant, and effective. Exactly what we needed for our website.", 5, "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("6", "James Wilson", "Founder", "CloudBase", "A must-have tool for any SaaS company looking to build trust with prospects.", 5, "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("7", "Anna Martinez", "Head of Growth", "Launchpad", "The carousel widget alone boosted our landing page conversions significantly.", 5, "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("8", "Robert Kim", "Product Lead", "DataSync", "Incredibly easy to set up and the design looks professional on any site.", 4, "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("9", "Priya Sharma", "CMO", "NexGen", "Our team loves how easy it is to collect and display customer stories.", 5, "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("10", "Tom Bradley", "Sales Director", "PipelineHQ", "The widget integration was seamless. Great product, great team!", 5, "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("11", "Natalie Brooks", "Design Lead", "PixelCraft", "Beautiful templates that match our brand perfectly. Highly recommend.", 5, "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("12", "Chris Evans", "Engineering Manager", "DevStack", "Rock-solid performance and the API is well-documented. Love it.", 4, "https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("13", "Sophie Turner", "Content Strategist", "MediaFlow", "Transformed our content strategy with authentic customer voices.", 5, "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("14", "Marcus Lee", "VP Product", "TechVault", "The analytics on widget performance are a game changer for our team.", 5, "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("15", "Rachel Green", "Operations Manager", "SwiftOps", "Streamlined our entire review collection process. Couldn't be happier.", 5, "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("16", "Alex Nguyen", "Co-Founder", "BrightPath", "One of the best investments we've made for building customer trust.", 5, "https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("17", "Diana Ross", "Brand Manager", "Luxe", "Elegant design options that elevate our brand presence online.", 4, "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=100&h=100&fit=crop&crop=face"),
	makeSampleReview("18", "Kevin Hart", "Growth Lead", "RocketScale", "Setup took 5 minutes and we were live. Incredible developer experience.", 5, "https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=100&h=100&fit=crop&crop=face"),
];

// Avatar size in pixels
const AVATAR_SIZE = 56;

function AvatarTooltip({ review, ratingStarColor }: { review: Review; ratingStarColor?: string }) {
	const customerInfo =
		review.customer_job_title && review.customer_company_name
			? `${review.customer_job_title} at ${review.customer_company_name}`
			: review.customer_headline || review.customer_company_name || "";

	return (
		<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
			<div className="bg-gray-900 text-white rounded-lg px-3 py-2 text-xs whitespace-nowrap shadow-lg">
				<p className="font-medium">{review.customer_name}</p>
				{customerInfo && <p className="text-gray-300 mt-0.5">{customerInfo}</p>}
				{review.rating > 0 && (
					<div className="flex items-center gap-0.5 mt-1">
						{[1, 2, 3, 4, 5].map((star) => (
							<svg
								key={star}
								className={`w-3 h-3 ${star <= review.rating ? (ratingStarColor ? "" : "text-amber-400") : "text-gray-600"}`}
								style={star <= review.rating && ratingStarColor ? { color: ratingStarColor } : undefined}
								fill={star <= review.rating ? "currentColor" : "none"}
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
								/>
							</svg>
						))}
					</div>
				)}
			</div>
			<div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-900 rotate-45" />
		</div>
	);
}

function ReviewPopover({
	review,
	onClose,
	onPlayVideo,
	ratingStarColor,
}: {
	review: Review;
	onClose: () => void;
	onPlayVideo: (playbackId: string) => void;
	ratingStarColor?: string;
}) {
	const popoverRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				onClose();
			}
		}
		function handleEsc(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", handleEsc);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleEsc);
		};
	}, [onClose]);

	const customerInfo =
		review.customer_job_title && review.customer_company_name
			? `${review.customer_job_title} at ${review.customer_company_name}`
			: review.customer_headline || review.customer_company_name || "";

	return (
		<div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 z-50" ref={popoverRef}>
			<div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-[340px] max-w-[90vw] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
				{/* Arrow */}
				<div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />

				{/* Video thumbnail */}
				{review.content_type === "video" && review.video_thumbnail_url && (
					<button
						type="button"
						onClick={() => review.mux_playback_id && onPlayVideo(review.mux_playback_id)}
						className="block w-full overflow-hidden relative cursor-pointer"
					>
						<img src={review.video_thumbnail_url} alt="" className="w-full h-44 object-cover" />
						<div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
							<div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: "#1f1f1f" }}>
								<svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
									<path d="M8 5v14l11-7z" />
								</svg>
							</div>
						</div>
					</button>
				)}

				<div className="p-4">
					{/* Author info */}
					<div className="flex items-center gap-3 mb-3">
						{review.customer_avatar_url ? (
							<img src={review.customer_avatar_url} alt={review.customer_name} className="w-10 h-10 rounded-full object-cover" />
						) : (
							<div
								className={`w-10 h-10 rounded-full bg-linear-to-br ${getGradient(review.customer_name)} flex items-center justify-center text-white text-xs font-medium shadow-lg`}
							>
								{getInitials(review.customer_name)}
							</div>
						)}
						<div className="flex-1 min-w-0">
							<p className="font-medium text-sm text-gray-900 truncate">{review.customer_name}</p>
							{customerInfo && <p className="text-xs text-gray-500 truncate">{customerInfo}</p>}
							{/* Rating */}
							{review.rating > 0 && (
								<div className="flex items-center gap-0.5 mt-1">
									{[1, 2, 3, 4, 5].map((star) => (
										<svg
											key={star}
											className={`w-4 h-4 ${star <= review.rating ? (ratingStarColor ? "" : "text-amber-400") : "text-gray-300"}`}
											style={star <= review.rating && ratingStarColor ? { color: ratingStarColor } : undefined}
											fill={star <= review.rating ? "currentColor" : "none"}
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
											/>
										</svg>
									))}
								</div>
							)}
						</div>
					</div>

					{/* Audio player */}
					{review.content_type === "audio" && review.audio_url && <AudioPlayer src={review.audio_url} initialDuration={review.audio_duration} />}

					{/* Content text */}
					{review.content_text && (
						<p className="text-sm leading-relaxed text-gray-600 line-clamp-4">{review.content_text}</p>
					)}
				</div>
			</div>
		</div>
	);
}

export default function TemplateClassicAvatarStack({ previewData, layoutConfig, templateDetail }: WidgetTemplateProps) {
	const fontStyle = useWidgetFont(layoutConfig, templateDetail);

	const getLayoutValue = useCallback(
		(key: string, fallback: unknown = "") => {
			if (layoutConfig[key] !== undefined) return layoutConfig[key];
			if (templateDetail?.defaults?.layout?.[key] !== undefined) return templateDetail.defaults.layout[key];
			return fallback;
		},
		[layoutConfig, templateDetail],
	);

	const containerRef = useRef<HTMLDivElement>(null);
	const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
	const [hoveredReviewId, setHoveredReviewId] = useState<string | null>(null);
	const [activeVideoPlaybackId, setActiveVideoPlaybackId] = useState<string | null>(null);
	const [maxFit, setMaxFit] = useState<number | null>(null);

	// Layout config values
	const avatarSize = Number(getLayoutValue("avatar_size", AVATAR_SIZE));
	const ratingStarColor = String(getLayoutValue("rating_star_color", "") || "") || undefined;
	const overlaySpacing = getLayoutValue("overlay_spacing", undefined);
	const overflowCountBgColor = String(getLayoutValue("overflow_count_bg_color", "") || "") || undefined;
	const overflowCountTextColor = String(getLayoutValue("overflow_count_text_color", "") || "") || undefined;

	const horizontalAlignment = String(getLayoutValue("horizontal_alignment", "center") || "center");
	const overlap = overlaySpacing !== undefined ? Number(overlaySpacing) : Math.round(avatarSize * 0.16);
	const effectiveAvatarWidth = avatarSize - overlap;
	// Extra space reserved for the "+N" badge when there's overflow
	const overflowBadgeWidth = avatarSize;

	// Reviews
	const reviews = useMemo(() => {
		if (!previewData) return sampleReviews;
		if (!("reviews" in previewData) || !previewData.reviews || previewData.reviews.data.length === 0) return sampleReviews;
		return previewData.reviews.data;
	}, [previewData]);

	// Total count from meta (includes reviews not loaded in current page)
	const totalCount = useMemo(() => {
		if (!previewData || !("reviews" in previewData) || !previewData.reviews) return reviews.length;
		return previewData.reviews.meta.counts.total;
	}, [previewData, reviews.length]);

	// Measure container and compute how many avatars fit
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const loadedCount = reviews.length;

		const compute = () => {
			const containerWidth = container.clientWidth;
			if (containerWidth <= 0) return;

			// First avatar takes full avatarSize, each subsequent takes effectiveAvatarWidth
			// If all fit (no overflow badge needed): totalWidth = avatarSize + (count - 1) * effectiveAvatarWidth
			const allFitWidth = avatarSize + (loadedCount - 1) * effectiveAvatarWidth;

			// Only skip overflow badge if ALL reviews fit (totalCount == loadedCount) AND they fit in space
			if (totalCount <= loadedCount && allFitWidth <= containerWidth) {
				setMaxFit(loadedCount);
				return;
			}

			// With overflow badge: first avatar + N avatars + badge
			// = avatarSize + (N - 1) * effectiveAvatarWidth + (overflowBadgeWidth - overlap)
			const availableForAvatars = containerWidth - (overflowBadgeWidth - overlap);
			const fitCount = Math.max(1, Math.floor((availableForAvatars - avatarSize) / effectiveAvatarWidth) + 1);
			setMaxFit(Math.min(fitCount, loadedCount));
		};

		compute();

		const resizeObserver = new ResizeObserver(compute);
		resizeObserver.observe(container);
		return () => resizeObserver.disconnect();
	}, [reviews.length, totalCount, avatarSize, effectiveAvatarWidth, overflowBadgeWidth, overlap]);

	const visibleReviews = useMemo(() => {
		if (maxFit === null) return reviews;
		return reviews.slice(0, maxFit);
	}, [reviews, maxFit]);
	const overflowCount = maxFit !== null ? Math.max(0, totalCount - maxFit) : 0;

	const handleAvatarClick = useCallback((reviewId: string) => {
		setActiveReviewId((prev) => (prev === reviewId ? null : reviewId));
	}, []);

	const handleClose = useCallback(() => {
		setActiveReviewId(null);
	}, []);

	return (
		<div ref={containerRef} className={`flex flex-col py-4 w-full ${horizontalAlignment === "left" ? "items-start" : horizontalAlignment === "right" ? "items-end" : "items-center"}`} style={fontStyle}>
			{/* Avatar Stack */}
			<div className={`flex items-center ${horizontalAlignment === "left" ? "justify-start" : horizontalAlignment === "right" ? "justify-end" : "justify-center"}`}>
				{visibleReviews.map((review, index) => (
					<div
						key={review.id}
						className="relative"
						style={{
							marginLeft: index === 0 ? 0 : `-${overlap}px`,
							zIndex: hoveredReviewId === review.id ? 40 : activeReviewId === review.id ? 30 : visibleReviews.length - index,
						}}
						onMouseEnter={() => setHoveredReviewId(review.id)}
						onMouseLeave={() => setHoveredReviewId(null)}
					>
						{/* Tooltip on hover */}
						{hoveredReviewId === review.id && activeReviewId !== review.id && (
							<AvatarTooltip review={review} ratingStarColor={ratingStarColor} />
						)}

						{/* Popover on click */}
						{activeReviewId === review.id && (
							<ReviewPopover
								review={review}
								onClose={handleClose}
								onPlayVideo={setActiveVideoPlaybackId}
								ratingStarColor={ratingStarColor}
							/>
						)}

						{/* Avatar button */}
						<button
							type="button"
							onClick={() => handleAvatarClick(review.id)}
							className="block relative rounded-full ring-2 ring-white cursor-pointer transition-transform duration-200 hover:scale-110 hover:-translate-y-1 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
							style={{ width: `${avatarSize}px`, height: `${avatarSize}px` }}
						>
							{review.customer_avatar_url ? (
								<img
									src={review.customer_avatar_url}
									alt={review.customer_name}
									className="w-full h-full rounded-full object-cover"
								/>
							) : (
								<div
									className={`w-full h-full rounded-full bg-linear-to-br ${getGradient(review.customer_name)} flex items-center justify-center text-white font-medium shadow-lg`}
									style={{ fontSize: `${Math.round(avatarSize * 0.32)}px` }}
								>
									{getInitials(review.customer_name)}
								</div>
							)}

							{/* Content type indicator badge */}
							{review.content_type !== "text" && (
								<span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center">
									{review.content_type === "video" ? (
										<svg className="w-2.5 h-2.5 text-gray-600 ml-px" fill="currentColor" viewBox="0 0 24 24">
											<path d="M8 5v14l11-7z" />
										</svg>
									) : (
										<svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m-4 0h8M12 3a4 4 0 014 4v4a4 4 0 01-8 0V7a4 4 0 014-4z" />
										</svg>
									)}
								</span>
							)}
						</button>
					</div>
				))}

				{/* Overflow count */}
				{overflowCount > 0 && (
					<div
						className="relative rounded-full ring-2 ring-white flex items-center justify-center font-medium"
						style={{
							width: `${avatarSize}px`,
							height: `${avatarSize}px`,
							marginLeft: `-${overlap}px`,
							zIndex: 0,
							fontSize: `${Math.round(avatarSize * 0.28)}px`,
							backgroundColor: overflowCountBgColor || "#1f1f1f",
							color: overflowCountTextColor || "#ffffff",
						}}
					>
						+{overflowCount}
					</div>
				)}
			</div>

			{/* Video Modal */}
			{activeVideoPlaybackId && (
				<VideoModal open={!!activeVideoPlaybackId} onOpenChange={(open) => !open && setActiveVideoPlaybackId(null)} playbackId={activeVideoPlaybackId} />
			)}
		</div>
	);
}
